import React, { useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Upload, X, Image, Check, Loader2, Trash2, Download } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ProofUploadModal - Modal pour uploader une preuve de vente
 * Permet de glisser-déposer ou sélectionner une image
 */
export const ProofUploadModal = ({ isOpen, onClose, ticket, onProofUploaded }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // Reset state when ticket changes or modal opens
  useEffect(() => {
    if (isOpen && ticket) {
      // Reset to ticket's existing proof or null
      setPreviewUrl(ticket.preuve_url || null);
      setSelectedFile(null);
      setIsDragging(false);
      setIsUploading(false);
    }
  }, [isOpen, ticket?.id, ticket?.preuve_url]);

  // Cleanup when modal closes
  const handleClose = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
    setIsDragging(false);
    setIsUploading(false);
    onClose();
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, []);

  const handleFileSelect = (file) => {
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Type de fichier non autorisé. Utilisez PNG, JPG ou WEBP.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 5MB)');
      return;
    }

    setSelectedFile(file);
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !ticket?.id) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(`${API_URL}/api/tickets/${ticket.id}/proof`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de l\'upload');
      }

      const result = await response.json();
      toast.success('Preuve uploadée avec succès !');
      
      if (onProofUploaded) {
        onProofUploaded(ticket.id, result.proof_url);
      }
      
      handleClose();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!ticket?.id) {
      toast.error('ID du ticket manquant');
      return;
    }
    
    // Confirmation simplifiée
    const confirmed = window.confirm('Supprimer cette preuve ?');
    if (!confirmed) return;

    setIsDeleting(true);
    try {
      console.log('Deleting proof for ticket:', ticket.id);
      const response = await fetch(`${API_URL}/api/tickets/${ticket.id}/proof`, {
        method: 'DELETE',
      });

      console.log('Delete response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Delete error response:', errorText);
        throw new Error('Erreur lors de la suppression');
      }
      
      const data = await response.json();
      console.log('Delete response data:', data);

      toast.success('Preuve supprimée !');
      setPreviewUrl(null);
      setSelectedFile(null);
      
      if (onProofUploaded) {
        onProofUploaded(ticket.id, null);
      }
      
      // Close the modal after deletion
      handleClose();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const existingProof = ticket?.preuve_url && !selectedFile;
  const fullProofUrl = existingProof ? `${API_URL}${ticket.preuve_url}` : previewUrl;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-zinc-900 border-[#C4A052]/30 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-[#C4A052] flex items-center gap-2">
            <Image className="h-5 w-5" />
            Preuve de vente - {ticket?.ref_vente || 'N/A'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Info ticket */}
          <div className="bg-zinc-800/50 rounded-lg p-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <span className="text-zinc-400">Match:</span>
              <span className="text-white truncate">{ticket?.evenement || 'N/A'}</span>
              <span className="text-zinc-400">Client:</span>
              <span className="text-white truncate">{ticket?.nom_client || 'N/A'}</span>
              <span className="text-zinc-400">Prix:</span>
              <span className="text-[#C4A052]">{ticket?.prix || 0} €</span>
            </div>
          </div>

          {/* Zone de drop / preview */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`
              relative border-2 border-dashed rounded-lg p-6 text-center transition-all
              ${isDragging ? 'border-[#C4A052] bg-[#C4A052]/10' : 'border-zinc-600 hover:border-zinc-500'}
              ${previewUrl ? 'p-2' : 'p-8'}
            `}
          >
            {previewUrl ? (
              <div className="relative">
                <img
                  src={fullProofUrl}
                  alt="Preuve"
                  className="max-h-64 mx-auto rounded-lg object-contain"
                />
                {/* Overlay buttons */}
                <div className="absolute top-2 right-2 flex gap-2">
                  {existingProof && (
                    <a
                      href={fullProofUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 bg-zinc-800/80 rounded-full hover:bg-zinc-700 transition-colors"
                      title="Ouvrir en grand"
                    >
                      <Download className="h-4 w-4 text-white" />
                    </a>
                  )}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (existingProof) {
                        handleDelete();
                      } else {
                        setPreviewUrl(null);
                        setSelectedFile(null);
                      }
                    }}
                    disabled={isDeleting}
                    className={`p-2 rounded-full transition-colors ${
                      isDeleting 
                        ? 'bg-zinc-600 cursor-wait' 
                        : 'bg-red-500/80 hover:bg-red-500'
                    }`}
                    title="Supprimer"
                  >
                    {isDeleting ? (
                      <Loader2 className="h-4 w-4 text-white animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 text-white" />
                    )}
                  </button>
                </div>
                {selectedFile && (
                  <p className="text-xs text-zinc-400 mt-2">
                    Nouveau fichier: {selectedFile.name}
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <Upload className="h-12 w-12 mx-auto text-zinc-500" />
                <div>
                  <p className="text-zinc-300 font-medium">
                    Glissez une image ici
                  </p>
                  <p className="text-zinc-500 text-sm">
                    ou cliquez pour sélectionner
                  </p>
                </div>
                <p className="text-xs text-zinc-600">
                  PNG, JPG, WEBP • Max 5MB
                </p>
              </div>
            )}

            {/* Hidden file input - only show when no preview */}
            {!previewUrl && (
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                onChange={handleInputChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
            >
              Fermer
            </Button>
            
            {selectedFile && (
              <Button
                onClick={handleUpload}
                disabled={isUploading}
                className="bg-[#C4A052] hover:bg-[#D4B86A] text-black"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Upload...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProofUploadModal;
